/**
 * Where each federal-tax packet goes — the one module that holds IRS fax
 * numbers and mailing addresses (spec 2026-09-26, section 4).
 *
 * Every entry names its IRS source page and the date it was checked. An entry
 * with `checkedOn: null` has NOT been confirmed against the source yet and must
 * be re-checked before the phase that first sends to it (spec section 16).
 */

export type DestinationChannel = "fax" | "mail" | "info";

export interface IrsDestination {
  channel: DestinationChannel;
  label: string;
  /** Present on fax destinations. */
  fax?: string;
  /** Present on mail destinations, one line per entry. */
  address?: readonly string[];
  /**
   * The street address a private delivery service (DHL, FedEx, UPS) uses when
   * `address` is a P.O. box the courier cannot deliver to.
   */
  courierAddress?: readonly string[];
  /** Present on info entries (for example the payment options page). */
  url?: string;
  source: string;
  checkedOn: string | null;
  note?: string;
}

export const IRS_DESTINATIONS = {
  irs_fax_foreign_owned_de: {
    channel: "fax",
    label:
      "IRS fax for Form 5472 with a pro forma 1120 (foreign-owned U.S. DE)",
    fax: "855-887-7737",
    source: "https://www.irs.gov/instructions/i5472",
    checkedOn: "2026-09-26",
    note: 'i5472 (Rev. December 2024), "Where to file": "Fax (300 DPI or higher) to 855-887-7737". Only the foreign-owned DE filing may be faxed; the IRS discards any other faxed return.',
  },
  irs_ogden_5472_foreign_owned_de: {
    channel: "mail",
    label: "Form 5472 with a pro forma 1120 by mail (foreign-owned U.S. DE)",
    address: [
      "Internal Revenue Service",
      "1973 Rulon White Blvd",
      "M/S 6112 Attn: PIN Unit",
      "Ogden, UT 84201",
      "USA",
    ],
    source: "https://www.irs.gov/instructions/i5472",
    checkedOn: "2026-09-26",
    note: 'i5472 (Rev. December 2024), "Where to file" (PDF page 2): the mail alternative to the fax. The ZIP is 84201 (the PIN Unit), not 84404. We fax; this is the fallback when a fax cannot go through.',
  },
  irs_austin_1040nr: {
    channel: "mail",
    label: "Form 1040-NR without a payment",
    address: [
      "Department of the Treasury",
      "Internal Revenue Service",
      "Austin, TX 73301-0215",
      "USA",
    ],
    source:
      "https://www.irs.gov/filing/international-where-to-file-forms-1040-nr-1040-pr-and-1040-ss-addresses-for-taxpayers-and-tax-professionals",
    checkedOn: "2026-09-26",
    note: 'Same address in the 2025 Instructions for Form 1040-NR, "Where To File" (pages 12-13). We mail without a payment: the customer pays the IRS directly.',
  },
  irs_charlotte_1040nr_payment: {
    channel: "mail",
    label: "Form 1040-NR with a payment enclosed",
    address: [
      "Internal Revenue Service",
      "P.O. Box 1303",
      "Charlotte, NC 28201-1303",
      "USA",
    ],
    source:
      "https://www.irs.gov/filing/international-where-to-file-forms-1040-nr-1040-pr-and-1040-ss-addresses-for-taxpayers-and-tax-professionals",
    checkedOn: "2026-09-26",
    note: "Same address in the 2025 Instructions for Form 1040-NR (pages 12-13). Only the customer's own instructions name it, for a check and Form 1040-V they mail themselves: we never enclose, mail or forward a payment.",
  },
  irs_itin_operation: {
    channel: "mail",
    label: "Form W-7 with the 1040-NR and certified identity documents",
    address: [
      "Internal Revenue Service",
      "ITIN Operation",
      "P.O. Box 149342",
      "Austin, TX 78714-9342",
      "USA",
    ],
    courierAddress: [
      "Internal Revenue Service",
      "ITIN Operation",
      "Mail Stop 6090-AUSC",
      "3651 S. Interregional, Hwy 35",
      "Austin, TX 78741-0000",
      "USA",
    ],
    source: "https://www.irs.gov/instructions/iw7",
    checkedOn: "2026-09-26",
    note: 'Instructions for Form W-7 (Rev. December 2024), "Where To Apply" (https://www.irs.gov/pub/irs-pdf/iw7.pdf p. 6): the P.O. box is the "By mail" address and `courierAddress` the "By private delivery services" one (re-checked 2026-09-27). "Attach Form W-7 to the front of your tax return" and "Leave the area of the SSN blank on the tax return".',
  },
  // Form 1120 "Where To File": 2025 Instructions for Form 1120, p. 4
  // (https://www.irs.gov/pub/irs-pdf/i1120.pdf), and the same rows on
  // https://www.irs.gov/filing/where-to-file-your-taxes-for-forms-1120 (page
  // last reviewed 20-Nov-2025; the old ".../for-form-1120" URL answers 404).
  // Both checked 2026-09-27. The PDF prints "Internal Revenue Service Center"
  // on the eastern $10 million-or-more Ogden row and "Internal Revenue
  // Service" on the western one; the web page prints the latter on both, and
  // the ZIP (84201-0012) is the same, so one entry serves both rows.
  irs_ogden_1120: {
    channel: "mail",
    label: "Form 1120 (principal business abroad or in a U.S. possession)",
    address: [
      "Internal Revenue Service",
      "P.O. Box 409101",
      "Ogden, UT 84409",
      "USA",
    ],
    source:
      "https://www.irs.gov/filing/where-to-file-your-taxes-for-forms-1120",
    checkedOn: "2026-09-27",
    note: 'i1120 (2025) p. 5, "Where To File": "A foreign country or U.S. territory", any amount (the web page: "A foreign country or U.S. possession").',
  },
  irs_kansas_city_1120: {
    channel: "mail",
    label: "Form 1120 (Kansas City)",
    address: [
      "Department of the Treasury",
      "Internal Revenue Service",
      "Kansas City, MO 64999-0012",
      "USA",
    ],
    source:
      "https://www.irs.gov/filing/where-to-file-your-taxes-for-forms-1120",
    checkedOn: "2026-09-27",
    note: 'i1120 (2025) p. 5, "Where To File": the eastern states and DC with total assets under $10 million and no Schedule M-3.',
  },
  irs_ogden_1120_domestic: {
    channel: "mail",
    label: "Form 1120 (Ogden)",
    address: [
      "Department of the Treasury",
      "Internal Revenue Service",
      "Ogden, UT 84201-0012",
      "USA",
    ],
    source:
      "https://www.irs.gov/filing/where-to-file-your-taxes-for-forms-1120",
    checkedOn: "2026-09-27",
    note: 'i1120 (2025) p. 5, "Where To File": the western states at any amount, and the eastern states and DC with total assets of $10 million or more or a Schedule M-3 filed.',
  },
  // Form 1065 "Where To File": 2025 Instructions for Form 1065, p. 6
  // (https://www.irs.gov/pub/irs-pdf/i1065.pdf), and the same rows on
  // https://www.irs.gov/filing/where-to-file-your-taxes-for-form-1065 (page
  // last reviewed 30-Jan-2026; it prints "Internal Revenue Service" without
  // "Center" and "U.S. possession" for "U.S. territory"). Both checked
  // 2026-09-27; the addresses below follow the PDF.
  irs_ogden_1065: {
    channel: "mail",
    label: "Form 1065 (Ogden)",
    address: [
      "Department of the Treasury",
      "Internal Revenue Service Center",
      "Ogden, UT 84201-0011",
      "USA",
    ],
    source: "https://www.irs.gov/pub/irs-pdf/i1065.pdf",
    checkedOn: "2026-09-27",
    note: 'i1065 (2025) p. 6, "Where To File": the western states at any amount, and the eastern states and DC with total assets of $10 million or more or a Schedule M-3 filed.',
  },
  irs_kansas_city_1065: {
    channel: "mail",
    label: "Form 1065 (Kansas City)",
    address: [
      "Department of the Treasury",
      "Internal Revenue Service Center",
      "Kansas City, MO 64999-0011",
      "USA",
    ],
    source: "https://www.irs.gov/pub/irs-pdf/i1065.pdf",
    checkedOn: "2026-09-27",
    note: 'i1065 (2025) p. 6, "Where To File": the eastern states and DC with total assets under $10 million and no Schedule M-3.',
  },
  irs_ogden_1065_foreign: {
    channel: "mail",
    label: "Form 1065 (principal business abroad or in a U.S. territory)",
    address: [
      "Internal Revenue Service",
      "P.O. Box 409101",
      "Ogden, UT 84409",
      "USA",
    ],
    source: "https://www.irs.gov/pub/irs-pdf/i1065.pdf",
    checkedOn: "2026-09-27",
    note: 'i1065 (2025) p. 6, "Where To File": "A foreign country or U.S. territory", any amount.',
  },
  // Form 7004 "Where To File": Instructions for Form 7004 (Rev. December
  // 2025) p. 5 (https://www.irs.gov/pub/irs-pdf/i7004.pdf) and
  // https://www.irs.gov/filing/where-to-file-form-7004 (page last reviewed
  // 27-Aug-2026), rows for Forms 1065, 1120 and 1120-S, checked 2026-10-17.
  // i7004 names no fax for these filers ("If you do not file electronically,
  // file Form 7004 with the Internal Revenue Service Center at the applicable
  // address"), so a paper 7004 for a C-Corp or a partnership goes by mail. A
  // foreign-owned U.S. DE does not use this table: i5472 sends its 7004 to
  // `irs_fax_foreign_owned_de` (or the PIN Unit address by mail).
  irs_ogden_7004_foreign: {
    channel: "mail",
    label: "Form 7004 (principal business abroad or in a U.S. territory)",
    address: [
      "Internal Revenue Service",
      "P.O. Box 409101",
      "Ogden, UT 84409",
      "USA",
    ],
    source: "https://www.irs.gov/pub/irs-pdf/i7004.pdf",
    checkedOn: "2026-10-17",
    note: 'i7004 (Rev. December 2025) p. 5, "Where To File", Forms 1065, 1120 and 1120-S: "A foreign country or U.S. territory".',
  },
  irs_kansas_city_7004: {
    channel: "mail",
    label: "Form 7004 (Kansas City)",
    address: [
      "Department of the Treasury",
      "Internal Revenue Service",
      "Kansas City, MO 64999-0019",
      "USA",
    ],
    source: "https://www.irs.gov/pub/irs-pdf/i7004.pdf",
    checkedOn: "2026-10-17",
    note: 'i7004 (Rev. December 2025) p. 5, "Where To File", Forms 1065, 1120 and 1120-S: the eastern states and DC with total assets at the end of the tax year under $10 million.',
  },
  irs_ogden_7004: {
    channel: "mail",
    label: "Form 7004 (Ogden)",
    address: [
      "Department of the Treasury",
      "Internal Revenue Service",
      "Ogden, UT 84201-0045",
      "USA",
    ],
    source: "https://www.irs.gov/pub/irs-pdf/i7004.pdf",
    checkedOn: "2026-10-17",
    note: 'i7004 (Rev. December 2025) p. 5, "Where To File", Forms 1065, 1120 and 1120-S: the western states at any amount, and the eastern states and DC with total assets of $10 million or more.',
  },
  // EIN record name corrections (NT-14). The IRS publishes no fax for a name
  // correction: a company-name correction the IRS cannot make by phone goes to
  // BMF Entity in writing (IRM 21.7.13.7.2.2, research 2026-10-22), routed by
  // campus. We send it by USPS Certified Mail with a return receipt.
  irs_bmf_entity_ogden: {
    channel: "mail",
    label: "EIN name correction letter (BMF Entity, Ogden)",
    address: [
      "Internal Revenue Service",
      "Stop 6273",
      "Ogden, UT 84201",
      "USA",
    ],
    source: "https://www.irs.gov/irm/part21/irm_21-007-013r",
    checkedOn: "2026-10-22",
    note: "IRM 21.7.13.7.2.2: BMF Entity unit at the Ogden campus. Used for a company formed in Wyoming or another Ogden state, and for any business located outside the United States. PostGrid verified the address on 2026-10-22.",
  },
  irs_bmf_entity_kansas_city: {
    channel: "mail",
    label: "EIN name correction letter (BMF Entity, Kansas City)",
    address: [
      "Internal Revenue Service",
      "333 W. Pershing Rd",
      "Mail Stop 6055 S-2",
      "Kansas City, MO 64108",
      "USA",
    ],
    source: "https://www.irs.gov/irm/part21/irm_21-007-013r",
    checkedOn: "2026-10-22",
    note: "IRM 21.7.13.7.2.2: BMF Entity unit at the Kansas City campus. Used for a company in a Kansas City state (Delaware among them).",
  },
  // Form 8822-B (Rev. December 2019) "Where To File", page 1: by the state of
  // the business's old address. Mail only; irs.gov lists no fax.
  irs_8822b_ogden: {
    channel: "mail",
    label: "Form 8822-B (Ogden)",
    address: [
      "Department of the Treasury",
      "Internal Revenue Service",
      "Ogden, UT 84201-0023",
      "USA",
    ],
    source: "https://www.irs.gov/pub/irs-pdf/f8822b.pdf",
    checkedOn: "2026-10-22",
    note: 'f8822b (Rev. December 2019) p. 1, "Where To File": the western states, and any place outside the United States.',
  },
  irs_8822b_kansas_city: {
    channel: "mail",
    label: "Form 8822-B (Kansas City)",
    address: [
      "Department of the Treasury",
      "Internal Revenue Service",
      "Kansas City, MO 64999",
      "USA",
    ],
    source: "https://www.irs.gov/pub/irs-pdf/f8822b.pdf",
    checkedOn: "2026-10-22",
    note: 'f8822b (Rev. December 2019) p. 1, "Where To File": the same eastern states and DC as the 1065/1120 Kansas City rows (`IRS_KANSAS_CITY_STATES`).',
  },
  irs_international_payments: {
    channel: "info",
    label: "How to pay the IRS from outside the US",
    url: "https://www.irs.gov/individuals/international-taxpayers/foreign-electronic-payments",
    source:
      "https://www.irs.gov/individuals/international-taxpayers/foreign-electronic-payments",
    checkedOn: "2026-09-26",
    note: 'Cited by the 2025 Instructions for Form 1040-NR, "Amount You Owe" (page 23): "If you want to pay electronically but don\'t have a U.S. bank account". A check or money order must be drawn on a U.S. financial institution. Shown when a 1040-NR ends with an amount owed.',
  },
} as const satisfies Record<string, IrsDestination>;

export type DestinationId = keyof typeof IRS_DESTINATIONS;

/**
 * i1065 (2025) p. 6 and i1120 (2025) p. 5 list the same states: principal
 * business in these states or DC, with total assets under $10 million and no
 * Schedule M-3 -> Kansas City. Form 8822-B (Rev. December 2019) lists the
 * same states under Kansas City.
 */
export const IRS_KANSAS_CITY_STATES: ReadonlySet<string> = new Set([
  "CT",
  "DE",
  "DC",
  "GA",
  "IL",
  "IN",
  "KY",
  "ME",
  "MD",
  "MA",
  "MI",
  "NH",
  "NJ",
  "NY",
  "NC",
  "OH",
  "PA",
  "RI",
  "SC",
  "TN",
  "VT",
  "VA",
  "WV",
  "WI",
]);

/**
 * i1065 (2025) p. 6 and i1120 (2025) p. 5: "A foreign country or U.S.
 * territory" -> P.O. Box 409101.
 */
const FOREIGN_OR_TERRITORY = new Set([
  "FOREIGN",
  "PR",
  "GU",
  "VI",
  "AS",
  "MP",
  "UM",
]);

type IrsCenter = "foreign" | "kansas_city" | "ogden";

/**
 * The decision the 1065, 1120 and 7004 tables share: a foreign country or U.S.
 * territory (and, where the table reads it so, no location at all) is the
 * foreign row; a Kansas City state goes to Kansas City when the table's size
 * and Schedule M-3 test allows it; everything else goes to Ogden.
 */
function irsCenterFor(
  principalState: string | null | undefined,
  rule: { emptyIsForeign: boolean; kansasCityEligible: boolean },
): IrsCenter {
  const state = (principalState ?? "").trim().toUpperCase();
  if ((rule.emptyIsForeign && !state) || FOREIGN_OR_TERRITORY.has(state)) {
    return "foreign";
  }
  if (rule.kansasCityEligible && IRS_KANSAS_CITY_STATES.has(state)) {
    return "kansas_city";
  }
  return "ogden";
}

const F1065_CENTERS = {
  foreign: "irs_ogden_1065_foreign",
  kansas_city: "irs_kansas_city_1065",
  ogden: "irs_ogden_1065",
} as const satisfies Record<IrsCenter, DestinationId>;

const F1120_CENTERS = {
  foreign: "irs_ogden_1120",
  kansas_city: "irs_kansas_city_1120",
  ogden: "irs_ogden_1120_domestic",
} as const satisfies Record<IrsCenter, DestinationId>;

const F7004_CENTERS = {
  foreign: "irs_ogden_7004_foreign",
  kansas_city: "irs_kansas_city_7004",
  ogden: "irs_ogden_7004",
} as const satisfies Record<IrsCenter, DestinationId>;

/**
 * The 1065 "Where To File" table (i1065 2025 p. 6): where the partnership's
 * principal business, office or agency is, its total assets at the end of the
 * year (Form 1065 item F), and whether Schedule M-3 is filed. The table keys
 * on Schedule M-3, never Schedule K-3.
 *
 * `principalState` is a two-letter U.S. state or DC code, a territory code
 * (PR, GU, VI, AS, MP, UM) or `"FOREIGN"`. A missing location answers Ogden
 * 84201, the address most of the table uses; the return model is what must
 * refuse a partnership whose location it does not know.
 */
export function destinationFor1065(
  principalState: string | null | undefined,
  totalAssets: number,
  filesScheduleM3: boolean,
): DestinationId {
  return F1065_CENTERS[
    irsCenterFor(principalState, {
      emptyIsForeign: false,
      kansasCityEligible: !filesScheduleM3 && totalAssets < 10_000_000,
    })
  ];
}

/**
 * The 1120 "Where To File" table (i1120 2025 p. 5,
 * https://www.irs.gov/pub/irs-pdf/i1120.pdf; the same rows on
 * https://www.irs.gov/filing/where-to-file-your-taxes-for-forms-1120): where
 * the corporation's principal business, office or agency is, its total assets
 * at the end of the year (Form 1120 item D), and whether Schedule M-3 is filed.
 *
 * `principalState` is a two-letter U.S. state or DC code, a territory code or
 * `"FOREIGN"`. An empty location answers the foreign row: a foreign-owned
 * corporation with no U.S. office has its principal business abroad. The
 * return model is what refuses a U.S. office whose state it does not know.
 */
export function destinationFor1120(
  principalState: string | null | undefined,
  totalAssets: number,
  filesScheduleM3: boolean,
): DestinationId {
  return F1120_CENTERS[
    irsCenterFor(principalState, {
      emptyIsForeign: true,
      kansasCityEligible: !filesScheduleM3 && totalAssets < 10_000_000,
    })
  ];
}

/**
 * Where a paper Form 7004 goes (i7004 Rev. December 2025 p. 5, "Where To
 * File"; https://www.irs.gov/filing/where-to-file-form-7004). A single-member
 * LLC is a foreign-owned U.S. DE: i5472 (Rev. December 2024), "Extension of
 * time to file": "The DE must fax or mail the Form 7004 to the fax number or
 * mailing address identified earlier ... For these entities, do not use the
 * regular filing address listed in the Instructions for Form 7004." A C-Corp
 * (Form 1120) or partnership (Form 1065) follows the i7004 table by where its
 * principal business, office or agency is and its total assets at the end of
 * the tax year; `principalState` is read like `destinationFor1120` (empty or
 * `"FOREIGN"` = run from abroad).
 */
export function destinationFor7004(
  kind: "smllc" | "mmllc" | "ccorp",
  principalState: string | null | undefined,
  totalAssets = 0,
): DestinationId {
  if (kind === "smllc") return "irs_fax_foreign_owned_de";
  return F7004_CENTERS[
    irsCenterFor(principalState, {
      emptyIsForeign: true,
      kansasCityEligible: totalAssets < 10_000_000,
    })
  ];
}
